// app/src/features/layout/AppFrame.jsx
// The slot props carry JSX that AppInner still owns; the frame renders them in a fixed order
// around the parts it owns, so each element keeps its DOM position (AppFrame.test.jsx checks it).
import ForecastHeader from "../../components/ForecastHeader.jsx";
import NavRail from "../../components/NavRail.jsx";
import PhoneApp from "../../components/phone/PhoneApp.jsx";
import TrackRecord from "../../components/TrackRecord.jsx";
import { TIER_ORDER, effectiveTier, tagOwner } from "../../lib/forecast/engines.js";
import { updateNowState, updateNowTier } from "../../lib/forecast/headerActions.js";
import { checkNowState, mayCheckAnswer } from "../../lib/forecast/answerCheck.js";
import { useResolverPoll } from "../../hooks/useResolverPoll.js";
import { useAnswerCheck } from "../resolution/useAnswerCheck.js";
import { useDeadlineClock } from "../resolution/useDeadlineClock.js";
import { reportAvailable } from "../../lib/forecast/selectors.js";
import {
  newestForecastMoment,
  withoutVerdictAtPastVersion,
} from "../../lib/forecast/reforecastEntries.js";
import { recordUiEvent } from "../../lib/logRingBuffer";
import { isConcluded } from "../../lib/tracking.js";
import TimelineColumn from "../timeline/TimelineColumn.jsx";
import ForecastManagerOverlay from "./ForecastManagerOverlay.jsx";

/** The phone layout, or the rail + timeline column row with its overlays and the Track record. */
export default function AppFrame({
  ctx,
  shareDialog,
  hero,
  runStatus,
  viewMomentBlock,
  tsdChart,
  trackingMarks,
  trackingSettingsOverlay,
  chatOverlay,
  signalBar,
  overlays,
  modals,
}) {
  const {
    CALIBRATION_MODEL_ID,
    active,
    animRef,
    bugFlash,
    calibration,
    contractBlink,
    contractOpen,
    debugForceAlertBadge,
    displayActive,
    dragRef,
    effectiveToday,
    forecastEngine,
    forecastHeaderRef,
    forecastOrder,
    forecastTracking,
    forecasts,
    forecastsLoaded,
    getTrackingPrefs,
    isPhone,
    isWatchlisted,
    liveSentinelIso,
    mkManageAccess,
    moveForecastToTop,
    openOnPhone,
    openReport,
    accountOpen,
    pendingPreprocess,
    proColors,
    proOpen,
    railPanelOpen,
    railSection,
    railTourFlash,
    reforecastEntries,
    runQuestionAsMine,
    runStatusOn,
    sayerCopy,
    selectRailSection,
    setAccountOpen,
    setContractOpen,
    setLogOpen,
    setProOpen,
    setSelEvt,
    setTrackingSettingsId,
    sharedForecasts,
    showReport,
    startNewQuestion,
    submitCustomTrigger,
    theme,
    titlePreprocessing,
    toggleRailCollapse,
    trackRecord,
    viewEntry,
    viewMomentIso,
    viewPrevVersion,
    viewSnap,
  } = ctx;
  // A closed question with no answer: where the search for it stands, and "Check now" for a
  // viewer who may edit the forecast (the resolve route requires it).
  const nowMs = effectiveToday.getTime();
  const answerCheck = useAnswerCheck({
    run: active,
    nowMs,
    enabled: !!forecastTracking && !viewSnap,
  });
  const { startResolverPoll } = useResolverPoll({
    forecastTracking,
    // Inside the poll, mockToday feeds only a value it discards, so Check now needs no debug clock.
    mockToday: null,
  });
  const mayCheck = mayCheckAnswer(active);
  // The hour after a check ends on its own, even with nothing else re-rendering the page.
  const checkClockMs = useDeadlineClock(
    Date.parse(active?.resolution?.check_again_at || "") || null,
  );
  // Resolves to the server's refusal ({status, body}) when it turned the check away, else null.
  const checkAnswerNow = async () => {
    let refusal = null;
    await startResolverPoll(active, { onSubmitFailed: (r) => (refusal = r) });
    return refusal;
  };
  // The version list's forecast before the shown one (null when there is none); with no list
  // (Forecast Tracking off), undefined lets the answer derive it from the forecast itself.
  const previousVersion = reforecastEntries.length ? viewPrevVersion : undefined;
  const updateNow = active
    ? updateNowState({
        run: active,
        forecastTracking,
        scrubbed: !!viewSnap,
        nowMs: effectiveToday.getTime(),
      })
    : null;
  // While an update runs, the header keeps the date's place, unseen, with the date it showed before
  // the run (the live moment without the run's own start stamp); the new date shows when it lands.
  const updatedAt = updateNow?.running
    ? (newestForecastMoment(active.history, active.reportPointers, { createdAt: active.createdAt })
        ?.iso ?? null)
    : liveSentinelIso;
  if (isPhone) {
    return (
      <PhoneApp
        forecasts={forecasts}
        sharedForecasts={sharedForecasts}
        forecastOrder={forecastOrder}
        isWatchlisted={isWatchlisted}
        loaded={forecastsLoaded}
        active={displayActive}
        previous={previousVersion}
        nowMs={effectiveToday.getTime()}
        updatedAt={active ? liveSentinelIso : null}
        concluded={isConcluded(active)}
        triggerResolutions={forecastTracking ? active?.triggerResolutions : null}
        reportEnabled={reportAvailable(active)}
        onOpenForecast={openOnPhone}
        onOpenReport={() => openReport(true)}
      />
    );
  }
  return (
    <>
      {shareDialog}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        <NavRail
          onNewQuestion={startNewQuestion}
          theme={theme}
          proColors={proColors}
          activeSection={railSection}
          onSelectSection={(id) => {
            dragRef.current = null;
            if (animRef.current) {
              cancelAnimationFrame(animRef.current);
              animRef.current = null;
            }
            const opening = railSection !== id;
            recordUiEvent({
              event: "ui.panel.toggle",
              panel: id === "forecasts" ? "log" : id === "question" ? "contract" : "chat",
              open: opening,
            });
            // Leaving the Forecast Manager closes its Tracking Settings flyover, as the classic
            // tab's toggle does.
            if (!(id === "forecasts" && opening)) setTrackingSettingsId(null);
            selectRailSection(id);
          }}
          flashSection={railTourFlash}
          // As the classic Signal Library bar is (IndicesBar `liftedOverIcons`): the report
          // and preprocess panels sit above the rail, so it is lifted over them to keep the
          // bug / settings / account controls reachable.
          liftedOverOverlay={showReport || !!pendingPreprocess}
          newQuestionActive={!active}
          onToggleCollapse={() => {
            recordUiEvent({ event: "ui.panel.toggle", panel: "rail", open: !railSection });
            if (railSection) setTrackingSettingsId(null);
            toggleRailCollapse();
          }}
          alertCount={
            debugForceAlertBadge
              ? 3
              : forecastTracking
                ? forecasts.filter((f) => f && f.alert).length
                : 0
          }
          bugFlash={bugFlash}
          trackRecordOpen={trackRecord.open}
          onTrackRecordToggle={() => {
            trackRecord.toggle();
            setProOpen(false);
            setAccountOpen(false);
          }}
          settingsOpen={proOpen}
          onSettingsToggle={() => {
            setProOpen((v) => !v);
            setAccountOpen(false);
            trackRecord.setOpen(false);
          }}
          accountOpen={accountOpen}
          onAccountToggle={() => {
            setAccountOpen((v) => {
              recordUiEvent({ event: "ui.account.toggle", open: !v });
              return !v;
            });
            setProOpen(false);
            trackRecord.setOpen(false);
          }}
        />
        <TimelineColumn
          ctx={ctx}
          answerBox={
            answerCheck
              ? {
                  state: answerCheck,
                  nowMs: Math.max(nowMs, checkClockMs),
                  checkNow: checkNowState(active, Math.max(nowMs, checkClockMs)),
                  onCheckNow: mayCheck ? checkAnswerNow : undefined,
                }
              : null
          }
          header={
            <>
              {active && (
                <ForecastHeader
                  measureRef={forecastHeaderRef}
                  forecast={withoutVerdictAtPastVersion(displayActive, {
                    viewMomentIso,
                    viewEntry,
                  })}
                  previous={previousVersion}
                  run={active}
                  // A first run in progress: the RunStatus card carries its stages and time, so
                  // the header keeps to the question and its dates.
                  minimal={runStatusOn}
                  title={
                    titlePreprocessing
                      ? null
                      : active.contract?.question ||
                        active.question ||
                        active.contract?.title ||
                        null
                  }
                  // Clear of the rail's open panel, which overlays this column from the left.
                  inset={railPanelOpen ? "var(--rail-panel-w)" : "0px"}
                  updatedAt={updatedAt}
                  viewingAt={viewSnap ? (viewSnap.generatedAt ?? viewMomentIso) : null}
                  nowMs={effectiveToday.getTime()}
                  questionOpen={contractOpen}
                  // Opens (never toggles shut) the Question panel, as the floating question did.
                  onOpenQuestion={() => {
                    if (!contractOpen) {
                      recordUiEvent({
                        event: "ui.panel.toggle",
                        panel: "contract",
                        open: true,
                      });
                    }
                    setTrackingSettingsId(null);
                    setContractOpen(true);
                  }}
                  onToggleQuestion={() => {
                    recordUiEvent({
                      event: "ui.panel.toggle",
                      panel: "contract",
                      open: !contractOpen,
                    });
                    setTrackingSettingsId(null);
                    setContractOpen(!contractOpen);
                  }}
                  questionBlink={contractBlink}
                  reportEnabled={reportAvailable(active)}
                  onOpenReport={() => openReport(true)}
                  onExploreTrigger={(ev) => setSelEvt({ ...ev, _rowId: "main" })}
                  // The timeline's double-click path, at its default depth; a rejected text reopens
                  // the trigger popover centred with the reason, as a chat-typed trigger's does.
                  onExploreCustom={(description, day) =>
                    submitCustomTrigger(active.id, description, day, null, {
                      screenX: Math.round(window.innerWidth / 2),
                      screenY: Math.round(window.innerHeight / 3),
                    })
                  }
                  onShare={mkManageAccess(active)}
                  cadenceIdx={forecastTracking ? getTrackingPrefs(active.id).cadIdx : null}
                  onOpenUpdates={() => {
                    setLogOpen(true);
                    setTrackingSettingsId(active.id);
                  }}
                  updateNow={updateNow}
                  onUpdateNow={(tier) => {
                    sayerCopy.updateNow(active, tier);
                    moveForecastToTop(active.id);
                  }}
                  // With no depth on the latest version, reforecast runs the composer's selection.
                  updateDepth={updateNowTier(active) ?? effectiveTier(forecastEngine)}
                  updateDepthOptions={TIER_ORDER.filter((t) => tagOwner(t))}
                  onRunAsMine={runQuestionAsMine ? () => runQuestionAsMine(active) : undefined}
                  answerCheck={answerCheck}
                />
              )}
            </>
          }
          hero={hero}
          runStatus={runStatus}
          viewMomentBlock={viewMomentBlock}
          tsdChart={tsdChart}
          trackingMarks={trackingMarks}
        >
          <ForecastManagerOverlay ctx={ctx} />
          {trackingSettingsOverlay}
          {chatOverlay}
          {signalBar}
        </TimelineColumn>
      </div>
      {overlays}
      {trackRecord.open && (
        <TrackRecord
          calibration={calibration}
          modelId={CALIBRATION_MODEL_ID}
          // Each depth's record is its current model's, as this browser resolves the tier.
          tiers={["quick", "deep"]
            .map((tag) => ({
              key: tag,
              label: tag === "quick" ? "Quick" : "Deep",
              modelId: tagOwner(tag),
            }))
            .filter((t) => t.modelId)}
          initialTier={effectiveTier(forecastEngine) === "deep" ? "deep" : "quick"}
          onClose={() => trackRecord.setOpen(false)}
        />
      )}
      {modals}
    </>
  );
}
